import React, { useMemo, useState } from 'react';
import { cx } from '@chakra-ui/utils';
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  Box,
  ChakraProvider,
} from '@chakra-ui/react';

import '../../assets/css/reset.css';

import { FailedToLoad } from '../ui-elements/FailedToLoad';
import { LocaleType, PaymentElementProps, PaymentMethodsType } from '../../@types';
import { useDynamicRefs } from '../../utils/dynRefs';
import { generateStripeTheme, generateMainTheme } from '../../utils/theme';
import { generatePaymentMethodsConfiguration } from '../../utils/paymentMethods';
import { usePaymentMethodStore, useCommonOptionsStore } from '../../store';
import { useTranslations } from '../../translations/useTranslations';
import { Gateway } from '../providers/Gateway';
import { AccordionHeader } from '../ui-elements/AccordionHeader';
import { PaymentMethodRegistry as registry } from '../PaymentMethodRegistry';
import { useSettings, SettingsResponseDataParams } from '../../queries/settings';
import { SupportedGateways } from '../../constants';

import { PaymentElementPropTypes } from './payment.prop-types';

const PaymentElement = React.forwardRef(({
  theme,
  ...props
}: PaymentElementProps, forwardRef: any) => {
  const {
    apiKey,
    propertyId,
    lang,
    onError,
    onLoadStart,
    onLoadEnd,
    onChange,
    amount,
    currency = 'usd',
  } = props;

  const [
    settings,
    isSettingsLoading,
    settingsErrors,
  ]: [SettingsResponseDataParams | null, boolean, string | null] = useSettings(apiKey, +propertyId);

  const [noPaymentMethodError, setNoPaymentMethodError] = useState<boolean>(false);
  const [accordionIndex, setAccordionIndex] = useState(-1);

  const [t] = useTranslations(lang as LocaleType);

  const setStripeTheme = useCommonOptionsStore(state => state.setStripeTheme);
  const setLang = useCommonOptionsStore(state => state.setLang);
  const setApiKey = useCommonOptionsStore(state => state.setApiKey);
  const setPropertyId = useCommonOptionsStore(state => state.setPropertyId);
  const [paymentMethod, setPaymentMethod] = usePaymentMethodStore(state => [
    state.paymentMethod,
    state.setPaymentMethod,
  ]);

  const paymentMethods = useMemo(() => (settings ? generatePaymentMethodsConfiguration(settings) : {}), [settings]);

  const clientKey = useMemo(
    () => ((settings?.gateway === SupportedGateways.STRIPE) ? settings.stripe.clientKey : ''),
    [settings],
  );
  const mainTheme = useMemo(() => generateMainTheme(theme), [theme]);
  const stripeTheme = useMemo(() => generateStripeTheme(theme.components, theme.foundations), [
    theme.components,
    theme.foundations,
  ]);

  const [defineRef, readRef] = useDynamicRefs();

  /* @
  * Reduce payment methods from registry to what host application requires.
  * Do not get the list of payment methods direclty from props
  * since it may(will) break the correct order of them on the form
  */
  const enabledPaymentMethods = React.useMemo(() => {
    const methods = paymentMethods ? Object.keys(paymentMethods) : [];
    return methods.length ? Object
      .keys(registry)
      .filter(pm => methods.includes(pm))
      // @ filter out those who needs amount and amound is not present
      .filter(pm => !registry[pm].hasAmount || Boolean(amount)) : [];
  }, [paymentMethods, amount]);

  /* @
  * Triggered when user clicks the accordeon payment method button
  */
  const onPaymentMethodChange = React.useCallback((expandedIndex: number) => {
    if (onLoadStart) onLoadStart();
    const method = enabledPaymentMethods[expandedIndex];
    setAccordionIndex(expandedIndex);
    setPaymentMethod(method);
    setNoPaymentMethodError(false);
    if (onChange) onChange({ name: 'payment_method', value: method });
    if (onLoadEnd) onLoadEnd();
  }, [enabledPaymentMethods, onLoadEnd, onLoadStart, setPaymentMethod, onChange]);

  /* @
  * Submit payment form from the upper scope.
  */
  const submit = () => {
    const formRef = readRef(paymentMethod);
    if (!paymentMethod || !formRef) {
      setNoPaymentMethodError(true);
      return false;
    }

    const submitFunc = (formRef.current as any)?.submit;
    if (submitFunc) return submitFunc();
    return true;
  };

  /* @
  * Validate payment form from the upper scope.
  */
  const validate = () => {
    const formRef = readRef(paymentMethod);
    if (!paymentMethod || !formRef) {
      setNoPaymentMethodError(true);
      return false;
    }

    const validateFunc = (formRef.current as any)?.validate;
    if (validateFunc) return validateFunc();

    return true;
  };

  /* @
  * Expose methods to the upper scope to let the host application work with the form.
  */
  React.useImperativeHandle(forwardRef, () => ({
    submit,
    validate,
  }));

  /* @
  * Set theme for stripe iframe if any
  */
  React.useEffect(() => {
    if (stripeTheme) setStripeTheme(stripeTheme);
  }, [setStripeTheme, stripeTheme]);

  /* @
  * Set property and apikey for secure communications
  */
  React.useEffect(() => {
    if (propertyId) setPropertyId(+propertyId);
    if (apiKey) setApiKey(apiKey);
  }, [
    setPropertyId,
    setApiKey,
    apiKey,
    propertyId,
  ]);

  /* @
  * Set translations for selected locale
  */
  React.useEffect(() => {
    if (lang) setLang(lang as LocaleType);
  }, [lang, setLang]);

  /* @
  * Set payment method as choosen if only one payment method is available.
  */
  React.useEffect(() => {
    if (enabledPaymentMethods.length === 1) onPaymentMethodChange(0);
  }, [onPaymentMethodChange, enabledPaymentMethods]);

  /* @
  * Handle error(s) when loading configuration for the propertly
  */
  React.useEffect(() => {
    if (onError && settingsErrors) onError(settingsErrors);
    // eslint-disable-next-line no-console
    if (settingsErrors) console.error(`Can not fetch settings: ${settingsErrors}`);
  }, [onError, settingsErrors]);

  /* @
  * Show no component if settings are being loaded or null,
  * Show error stub is settings can't be loaded.
  */
  if (isSettingsLoading) return null;
  if (settingsErrors) return <FailedToLoad />;
  if (!settings) return null;

  return (
    <ChakraProvider theme={mainTheme}>
      {enabledPaymentMethods.length > 1 && (
        <Box mb={3} fontSize="sm" color={noPaymentMethodError ? 'error.500' : ''}>
          <>{t('selectPaymentMethod')}</>
        </Box>
      )}
      <Accordion
        onChange={onPaymentMethodChange}
        className={cx('TabsContainer')}
        index={accordionIndex}>
        {enabledPaymentMethods.map(
          (pm, idx) => {
            const { Component, Title, Icon } = registry[pm];
            return (
              <AccordionItem className={cx('TabContainer')} key={idx}>
                {({ isExpanded }) => (
                  <>
                    <AccordionHeader name={pm} title={Title} isExpanded={isExpanded}>
                      <Icon />
                    </AccordionHeader>
                    <AccordionPanel pb={4}>
                      {paymentMethod === pm && (
                      <Gateway
                      gateway={settings.gateway}
                      method={pm as PaymentMethodsType}
                      clientKey={clientKey}
                      options={{ amount, currency }}
                    >
                        <Component
                        pm={pm as PaymentMethodsType}
                        {...props}
                        gateway={settings.gateway}
                        {...(paymentMethods as any)[pm]} ref={defineRef(pm)}
                      />
                      </Gateway>
                      )}
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            );
          },
        )}
      </Accordion>
    </ChakraProvider>
  );
});

PaymentElement.propTypes = PaymentElementPropTypes;

export { PaymentElement };
